import { Box, Card, CardContent, Skeleton, Typography } from "@mui/material";
import type { JSX } from "react";

import { SectionCardHeader } from "../../shared.js";
import type { AgentName, AgentStatusRow } from "../../types.js";
import { RunPill } from "./atoms.js";
import { agoFmt, untilFmt, type AgentState } from "./statusHelpers.js";

// ── The eight loops, in operator-meaningful order ────────────────────

const LOOP_META: Record<AgentName, { label: string; blurb: string }> = {
  trend_scan: {
    label: "Discover topics",
    blurb: "Scans industry news + the knowledge base for things worth posting about",
  },
  content_gen: {
    label: "Draft content",
    blurb: "Writes drafts from the top topics (only when autonomous drafting is on)",
  },
  scheduler: {
    label: "Schedule posts",
    blurb: "Slots approved drafts into the next open posting windows",
  },
  publisher: {
    label: "Publish posts",
    blurb: "Pushes due posts to the connected platforms (when publishing is on)",
  },
  press_release_detector: {
    label: "Watch for news",
    blurb: "Flags newsworthy company events for a press release",
  },
  company_research: {
    label: "Research XOP",
    blurb: "Refreshes the grounded company facts it writes from",
  },
  perf_tracker: {
    label: "Track performance",
    blurb: "Rolls up engagement on what's been published",
  },
  sales_sync: {
    label: "Sync sales signals",
    blurb: "Promotes hot signals from the sales agent into topics",
  },
};

const LOOP_ORDER: AgentName[] = [
  "trend_scan",
  "content_gen",
  "scheduler",
  "publisher",
  "press_release_detector",
  "company_research",
  "perf_tracker",
  "sales_sync",
];

/** Schedule card: each background loop, when it last ran, and when it's due again. */
export function ScheduleCard({
  rows,
  state,
}: {
  rows: AgentStatusRow[] | undefined;
  state: AgentState;
}): JSX.Element {
  const byName = new Map<AgentName, AgentStatusRow>();
  for (const r of rows ?? []) byName.set(r.name, r);

  const note =
    state === "paused"
      ? "Paused — these won't run until you resume the agent."
      : state === "sleeping"
        ? "Drafting is asleep until the budget resets; publishing of already-approved posts continues."
        : null;

  return (
    <Card data-testid="status-schedule">
      <SectionCardHeader
        title="What happens next"
        subtitle="Each background loop, when it last ran, and when it's due again."
      />
      <CardContent sx={{ px: 2.5, py: 2, display: "grid", gap: 1.25 }}>
        {note && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: state === "paused" ? "warning.main" : "info.main",
            }}
          >
            {note}
          </Typography>
        )}
        {!rows ? (
          <Skeleton variant="rounded" height={160} />
        ) : (
          <Box sx={{ display: "grid", gap: 1.25 }}>
            {LOOP_ORDER.filter((n) => byName.has(n)).map((name) => {
              const row = byName.get(name)!;
              const meta = LOOP_META[name];
              return (
                <Box
                  key={name}
                  data-testid={`schedule-row-${name}`}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {meta.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      {meta.blurb}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      textAlign: "right",
                      display: "grid",
                      gap: 0.25,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.75,
                      }}
                    >
                      {row.last_run ? (
                        <RunPill status={row.last_run.status} />
                      ) : null}
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "text.secondary",
                          fontFamily: "ui-monospace, monospace",
                        }}
                      >
                        {agoFmt(row.last_run?.finished_at ?? null)}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: state === "running" ? "text.primary" : "text.secondary",
                      }}
                    >
                      next {untilFmt(row.next_scheduled_at)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
